import { Check, Clock, LogOut, X } from 'lucide-react'
import { Navigate } from 'react-router-dom'
import { Logo } from '../components/brand/Logo'
import { Button } from '../components/ui/button'
import { useAuth } from '../context/AuthContext'
import { cn } from '../lib/utils'
import { USER_ROLES } from '../types/auth'

const DEFAULT_SUPPORT_EMAIL = 'abc@gmail.com'

export function AccountStatusPage() {
  const { session, logout, isLoading } = useAuth()
  const status = session?.user.status
  const supportEmail =
    session?.doctor_support_email?.trim() || DEFAULT_SUPPORT_EMAIL
  const rejectionReason = session?.user.rejection_reason?.trim()

  if (isLoading && !session) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-parch-50 text-sm text-ink-600">
        Loading your session...
      </div>
    )
  }

  if (!session) {
    return <Navigate to="/login" replace />
  }

  if (session.role !== USER_ROLES.DOCTOR || status === 'approved') {
    return <Navigate to={session.dashboard_path ?? '/dashboard'} replace />
  }

  const isRejected = status === 'rejected'

  return (
    <div className="min-h-screen bg-parch-50">
      <header className="flex items-center justify-between border-b border-parch-100 bg-white px-4 py-3 sm:px-6">
        <Logo size="sm" linkToHome={false} />
        <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">
          Signed in
        </span>
      </header>

      <main className="mx-auto max-w-lg px-4 py-12 text-center sm:py-16">
        <div
          className={cn(
            'mx-auto mb-5 grid h-[4.25rem] w-[4.25rem] place-items-center rounded-full border',
            isRejected
              ? 'border-danger/20 bg-danger/10 text-danger'
              : 'border-green-200 bg-green-100 text-green-700',
          )}
          aria-hidden="true"
        >
          {isRejected ? <X className="h-7 w-7" /> : <Clock className="h-7 w-7" />}
        </div>

        <h1 className="font-display text-3xl font-bold tracking-tight text-ink-900 sm:text-[2rem]">
          {isRejected ? 'Application not approved' : "We're reviewing your application"}
        </h1>
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-ink-600 sm:text-[0.95rem]">
          {isRejected
            ? "You're signed in, but this account can't open PeerMed until an administrator reviews the decision with you."
            : "You're signed in. PeerMed stays locked until a profile reviewer approves your account — we'll email you when it's ready."}
        </p>

        {isRejected ? (
          rejectionReason ? (
            <div className="mt-6 rounded-xl border border-danger/20 bg-[#fdf6f4] px-4 py-3 text-left text-sm text-ink-700">
              <p className="font-semibold text-ink-900">Reason from reviewer</p>
              <p className="mt-1 leading-relaxed">{rejectionReason}</p>
            </div>
          ) : null
        ) : (
          <div className="mt-7 rounded-2xl border border-parch-100 bg-white p-4 text-left shadow-card sm:p-5">
            <div className="grid grid-cols-[1.75rem_minmax(0,1fr)] gap-3 py-3">
              <span className="mt-0.5 grid h-6 w-6 place-items-center rounded-full bg-green-600 text-white">
                <Check className="h-3.5 w-3.5" strokeWidth={3} />
              </span>
              <div>
                <p className="text-sm font-semibold text-ink-900">Application received</p>
                <p className="mt-0.5 text-xs leading-relaxed text-ink-500">
                  Your signup and credentials are on file.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-[1.75rem_minmax(0,1fr)] gap-3 border-t border-parch-100 py-3">
              <span className="mt-0.5 grid h-6 w-6 place-items-center rounded-full bg-green-100 text-xs font-bold text-green-700 shadow-[0_0_0_2px_theme(colors.green.600)]">
                2
              </span>
              <div>
                <p className="text-sm font-semibold text-green-900">Under review</p>
                <p className="mt-0.5 text-xs leading-relaxed text-ink-500">
                  A profile reviewer is checking your details. This usually takes 1–2 business days.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-[1.75rem_minmax(0,1fr)] gap-3 border-t border-parch-100 py-3">
              <span className="mt-0.5 grid h-6 w-6 place-items-center rounded-full bg-parch-100 text-xs font-bold text-ink-400">
                3
              </span>
              <div>
                <p className="text-sm font-semibold text-ink-900">Full access</p>
                <p className="mt-0.5 text-xs leading-relaxed text-ink-500">
                  After approval you can open your dashboard and use PeerMed normally.
                </p>
              </div>
            </div>
          </div>
        )}

        <p className="mt-5 text-sm text-ink-600">
          Need help?{' '}
          <a className="font-semibold text-green-700 hover:underline" href={`mailto:${supportEmail}`}>
            {supportEmail}
          </a>
        </p>

        <div className="mt-6 flex justify-center">
          <Button type="button" variant="secondary" onClick={() => void logout()}>
            <LogOut className="h-4 w-4" />
            Sign Out
          </Button>
        </div>
      </main>
    </div>
  )
}
